import React from 'react';
import { useTranslation, useEnvironment } from '@wix/yoshi-flow-editor';
import { st, classes } from './SchedulingAgenda.st.css';
import { DailySessions } from './DailySessions/DailySessions';
import { SessionDetails } from './SessionDetails/SessionDetails';
import Divider from './Divider/Divider';
import {
  SchedulingSectionStatus,
  SchedulingSectionViewModel,
} from '../../../../service-page-view-model/scheduling-section-view-model/schedulingSectionViewModel';
import { Loader } from './Loader/Loader';
import { EmptyState } from './EmptyState/EmptyState';
import { useSettings } from '@wix/tpa-settings/react';
import settingsParams from '../../settingsParams';
import { useLoadAllSessions } from './useLoadAllSessions';
import { CurrentLocationProvider } from './useCurrentLocation';

export type SchedulingAgendaProps = {
  viewModel?: SchedulingSectionViewModel;
  currentLocation?: string;
};
export const SchedulingAgenda: React.FC<SchedulingAgendaProps> = ({
  viewModel,
  currentLocation,
}) => {
  const settings = useSettings();
  const { t } = useTranslation();
  const { isRTL } = useEnvironment();
  const totalNumberOfSessions = viewModel?.schedulingDaysViewModel?.length || 0;
  const { numberOfVisibleSessions, LoadAllSessions } = useLoadAllSessions({
    defaultValue: settingsParams.scheduleDays,
    totalNumberOfSessions,
  });

  const componentByStatus = () => {
    switch (viewModel?.status) {
      case SchedulingSectionStatus.LOADING:
        return <Loader />;
      case SchedulingSectionStatus.EMPTY:
        return emptyState();
      case SchedulingSectionStatus.FAILED:
        return (
          <EmptyState
            message={t('app.widget.uou-messages.scheduling-error')}
            dataHook="error-message"
          />
        );
      case SchedulingSectionStatus.SUCCESS:
        return dailySessions();
      default:
        return <Loader />;
    }
  };
  const emptyState = () => {
    const emptyStateMessage = settings.get(
      settingsParams.scheduleEmptyStateText,
    );
    return <EmptyState message={emptyStateMessage} dataHook="empty-state" />;
  };

  const showDivider = (index: number) => {
    const lastElementIndex =
      Math.min(numberOfVisibleSessions, totalNumberOfSessions) - 1;
    return index !== lastElementIndex ? <Divider /> : null;
  };

  const multiDaySessions = () => {
    const sessions = viewModel?.multiDaySessionsViewModel;
    if (!sessions?.length) {
      return null;
    }
    return (
      <ul className={classes.list} data-hook="multi-day-sessions">
        {sessions.map((session, index) => (
          <React.Fragment key={index}>
            <li data-hook="multi-day-session">
              <SessionDetails {...session} />
            </li>
            {index !== sessions.length - 1 && <li role="separator"><Divider /></li>}
          </React.Fragment>
        ))}
      </ul>
    );
  };

  const dailySessions = () => {
    return (
      <>
        {multiDaySessions()}
        <ul className={classes.list}>
          {viewModel?.schedulingDaysViewModel
            ?.slice(0, numberOfVisibleSessions)
            .map((scheduleDay, index) => (
              <React.Fragment key={index}>
                <DailySessions viewModel={scheduleDay} />
                {showDivider(index)}
              </React.Fragment>
            ))}
          <LoadAllSessions />
        </ul>
      </>
    );
  };

  return (
    <CurrentLocationProvider value={currentLocation}>
      <div
        className={st(classes.root, {
          isRTL,
        })}
        data-hook="scheduling-agenda"
      >
        {componentByStatus()}
      </div>
    </CurrentLocationProvider>
  );
};
